import { useState } from 'react'
import { useWeather } from '../hooks/useWeather'

const WeatherApp = () => {
  const { 
    weatherData, 
    location, 
    forecast, 
    loading, 
    error, 
    locationLoading, 
    refreshWeather, 
    changeLocation, 
    searchCities, 
    requestLocation,
    isDemo 
  } = useWeather()
  
  const [activeTab, setActiveTab] = useState('current') // current, forecast, settings
  const [locationSearch, setLocationSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showLocationSearch, setShowLocationSearch] = useState(false)

  // Handle location search
  const handleLocationSearch = async (query) => {
    setLocationSearch(query)
    if (query.length > 2) {
      const results = await searchCities(query)
      setSearchResults(results)
    } else {
      setSearchResults([])
    }
  }

  // Handle location selection
  const handleLocationSelect = (selectedLocation) => {
    changeLocation(selectedLocation)
    setShowLocationSearch(false)
    setLocationSearch('')
    setSearchResults([])
  }

  // Get weather icon class
  const getWeatherIcon = (condition, large = false) => {
    const size = large ? 'text-6xl' : 'text-2xl'
    switch (condition?.toLowerCase()) {
      case 'clear':
        return `fas fa-sun text-yellow-400 ${size}`
      case 'clouds':
        return `fas fa-cloud text-gray-300 ${size}`
      case 'rain':
        return `fas fa-cloud-rain text-blue-400 ${size}`
      case 'drizzle':
        return `fas fa-cloud-rain text-blue-300 ${size}`
      case 'thunderstorm':
        return `fas fa-bolt text-yellow-300 ${size}`
      case 'snow':
        return `fas fa-snowflake text-blue-200 ${size}`
      case 'mist':
      case 'fog':
        return `fas fa-smog text-gray-400 ${size}`
      default:
        return `fas fa-sun text-yellow-400 ${size}`
    }
  }

  // Format date for forecast
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-blue-400 text-6xl mb-4"></i>
          <h2 className="text-2xl font-bold mb-2">Loading Weather</h2>
          <p className="text-white/70">Getting your location...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !weatherData) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-red-400 text-6xl mb-4"></i>
          <h2 className="text-2xl font-bold mb-2">Weather Unavailable</h2>
          <p className="text-white/70 mb-4">{error}</p>
          <button 
            onClick={refreshWeather}
            className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
          >
            <i className="fas fa-refresh mr-2"></i>
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-black/20 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-bold">Weather</h1>
          {isDemo && (
            <span className="px-2 py-1 bg-pink-500/20 text-pink-400 text-xs rounded-full">
              Demo Mode
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {locationLoading ? (
            <i className="fas fa-spinner fa-spin text-white/60"></i>
          ) : (
            <button 
              onClick={refreshWeather}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Refresh weather"
            >
              <i className="fas fa-sync-alt text-white/60"></i>
            </button>
          )}
          <button 
            onClick={() => setShowLocationSearch(!showLocationSearch)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Change location"
          >
            <i className="fas fa-map-marker-alt text-white/60"></i>
          </button>
        </div>
      </div>

      {/* Location Search Modal */}
      {showLocationSearch && (
        <div className="p-4 border-b border-white/10 bg-white/5">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for a city..."
              value={locationSearch}
              onChange={(e) => handleLocationSearch(e.target.value)}
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-blue-400"
              autoFocus
            />
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-black/80 border border-white/20 rounded-lg max-h-40 overflow-y-auto z-10">
                {searchResults.map((city, index) => (
                  <button
                    key={index}
                    onClick={() => handleLocationSelect(city)}
                    className="w-full p-3 text-left hover:bg-white/10 transition-colors text-sm"
                  >
                    {city.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-3">
            <button
              onClick={requestLocation}
              className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-sm"
              disabled={locationLoading}
            >
              <i className="fas fa-crosshairs mr-2"></i>
              Use My Location
            </button>
            <button
              onClick={() => setShowLocationSearch(false)}
              className="px-3 py-2 bg-gray-500/20 hover:bg-gray-500/30 rounded-lg transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setActiveTab('current')}
          className={`flex-1 p-3 text-sm font-medium transition-colors ${
            activeTab === 'current'
              ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-400'
              : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <i className="fas fa-thermometer-half mr-2"></i>
          Current
        </button>
        <button
          onClick={() => setActiveTab('forecast')}
          className={`flex-1 p-3 text-sm font-medium transition-colors ${
            activeTab === 'forecast'
              ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-400'
              : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <i className="fas fa-calendar-week mr-2"></i>
          Forecast
        </button>
        <button
          onClick={() => setActiveTab('details')}
          className={`flex-1 p-3 text-sm font-medium transition-colors ${
            activeTab === 'details'
              ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-400'
              : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <i className="fas fa-info-circle mr-2"></i>
          Details
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'current' && weatherData && (
          <div className="space-y-6">
            {/* Current Weather Header */}
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <i className={getWeatherIcon(weatherData.condition, true)}></i>
              </div>
              <h2 className="text-4xl font-light mb-2">{weatherData.temperature}°F</h2>
              <p className="text-xl text-white/80 capitalize mb-2">{weatherData.description}</p>
              <p className="text-white/60">
                <i className="fas fa-map-marker-alt mr-2"></i>
                {weatherData.location}
              </p>
              <p className="text-sm text-white/50 mt-2">
                Feels like {weatherData.feelsLike}°F
              </p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <i className="fas fa-arrow-up text-red-400 text-lg mb-2"></i>
                <p className="text-lg font-medium">{weatherData.high}°F</p>
                <p className="text-xs text-white/60">High</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <i className="fas fa-arrow-down text-blue-400 text-lg mb-2"></i>
                <p className="text-lg font-medium">{weatherData.low}°F</p>
                <p className="text-xs text-white/60">Low</p>
              </div>
            </div>

            {/* Weather Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-tint text-blue-400"></i>
                    <span className="text-sm text-white/70">Humidity</span>
                  </div>
                  <span className="font-medium">{weatherData.humidity}%</span>
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-wind text-green-400"></i>
                    <span className="text-sm text-white/70">Wind</span>
                  </div>
                  <span className="font-medium">{weatherData.windSpeed} mph</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'forecast' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">5-Day Forecast</h3>
            {forecast && forecast.length > 0 ? (
              forecast.map((day, index) => (
                <div key={index} className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-center min-w-16">
                        <p className="text-sm font-medium">{formatDate(day.date)}</p>
                      </div>
                      <i className={getWeatherIcon(day.condition)}></i>
                      <div>
                        <p className="font-medium capitalize">{day.condition}</p>
                        <p className="text-xs text-white/60 capitalize">{day.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-medium">
                        <span className="text-red-400">{day.high}°</span>
                        <span className="text-white/40 mx-1">/</span>
                        <span className="text-blue-400">{day.low}°</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-white/50 py-8">
                <i className="fas fa-calendar-times text-3xl mb-4"></i>
                <p>Forecast data unavailable</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'details' && weatherData && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Weather Details</h3>
            <div className="grid gap-4">
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-thermometer-half text-orange-400"></i>
                    <span>Feels Like</span>
                  </div>
                  <span className="font-medium">{weatherData.feelsLike}°F</span>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-compress-arrows-alt text-purple-400"></i>
                    <span>Pressure</span>
                  </div>
                  <span className="font-medium">{weatherData.pressure} hPa</span>
                </div>
              </div>
              
              {weatherData.visibility && (
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <i className="fas fa-eye text-cyan-400"></i>
                      <span>Visibility</span>
                    </div>
                    <span className="font-medium">{weatherData.visibility} mi</span>
                  </div>
                </div>
              )}
              
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-sun text-yellow-400"></i>
                    <span>Sunrise</span>
                  </div>
                  <span className="font-medium">
                    {weatherData.sunrise && weatherData.sunrise instanceof Date 
                      ? weatherData.sunrise.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-moon text-blue-300"></i>
                    <span>Sunset</span>
                  </div>
                  <span className="font-medium">
                    {weatherData.sunset && weatherData.sunset instanceof Date 
                      ? weatherData.sunset.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default WeatherApp
